import React, { useState, useMemo, useCallback } from 'react';
import { ClientPerformanceData } from '../../types';
import clientPerformanceApi from '../../services/clientPerformanceApi';

interface PerformanceTableProps {
  data: ClientPerformanceData[];
}

type SortField = keyof ClientPerformanceData;
type SortDirection = 'asc' | 'desc';

const PerformanceTable: React.FC<PerformanceTableProps> = ({ data }) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      // First sort by date and company name for grouping
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return sortDirection === 'desc' ? -dateCompare : dateCompare;
      
      const companyCompare = a.company_name.localeCompare(b.company_name);
      if (companyCompare !== 0) return companyCompare;
      
      // Then apply the selected sort field
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Handle numeric sorting for volume and percentage fields
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle percentage string sorting
      if (typeof aValue === 'string' && aValue.includes('%') && 
          typeof bValue === 'string' && bValue.includes('%')) {
        const aNum = parseFloat(aValue.replace('%', ''));
        const bNum = parseFloat(bValue.replace('%', ''));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      // Handle string sorting
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sorted;
  }, [data, sortField, sortDirection]);

  // Determine which rows are first in their group
  const groupedRowInfo = useMemo(() => {
    const info = new Map<number, { isFirstInGroup: boolean; groupKey: string }>();
    let lastGroupKey = '';
    
    sortedData.forEach((item, index) => {
      const groupKey = `${item.date}_${item.company_name}`;
      const isFirstInGroup = groupKey !== lastGroupKey;
      info.set(index, { isFirstInGroup, groupKey });
      lastGroupKey = groupKey;
    });
    
    return info;
  }, [sortedData]);

  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const getPerformanceClass = (value: string, type: 'inbox' | 'spam' | 'open' | 'reputation') => {
    if (type === 'reputation') {
      const level = clientPerformanceApi.getDomainReputationLevel(value);
      return `performance-${level}`;
    }
    const level = clientPerformanceApi.getPerformanceLevel(value, type);
    return `performance-${level}`;
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <span className="sort-icon">⇅</span>;
    }
    return <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="performance-table-container">
      <table className="performance-table">
        <thead>
          <tr>
            <th className="th-expand"></th>
            <th onClick={() => handleSort('date')} className="sortable">
              Date <SortIcon field="date" />
            </th>
            <th onClick={() => handleSort('company_name')} className="sortable">
              Company <SortIcon field="company_name" />
            </th>
            <th onClick={() => handleSort('domain')} className="sortable">
              Domain <SortIcon field="domain" />
            </th>
            <th onClick={() => handleSort('total_volume')} className="sortable">
              Volume <SortIcon field="total_volume" />
            </th>
            <th onClick={() => handleSort('overall_inbox_pct')} className="sortable">
              Inbox % <SortIcon field="overall_inbox_pct" />
            </th>
            <th onClick={() => handleSort('spam_rate')} className="sortable">
              Spam % <SortIcon field="spam_rate" />
            </th>
            <th onClick={() => handleSort('or_pct')} className="sortable">
              Open % <SortIcon field="or_pct" />
            </th>
            <th onClick={() => handleSort('cr_pct')} className="sortable">
              Click % <SortIcon field="cr_pct" />
            </th>
            <th onClick={() => handleSort('domain_reputation')} className="sortable">
              Reputation <SortIcon field="domain_reputation" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => {
            const rowInfo = groupedRowInfo.get(index);
            const isFirstInGroup = rowInfo?.isFirstInGroup || false;
            
            return (
              <React.Fragment key={index}>
                <tr className={`main-row ${isFirstInGroup ? 'group-first' : 'group-member'}`}>
                  <td className="td-expand">
                    <button
                      className="expand-button"
                      onClick={() => toggleRowExpansion(index)}
                      aria-label={expandedRows.has(index) ? 'Collapse' : 'Expand'}
                    >
                      {expandedRows.has(index) ? '−' : '+'}
                    </button>
                  </td>
                  <td className={isFirstInGroup ? 'group-date' : 'group-date-empty'}>
                    {isFirstInGroup ? item.date : ''}
                  </td>
                  <td className={isFirstInGroup ? 'company-name' : 'company-name-empty'}>
                    {isFirstInGroup ? item.company_name : ''}
                  </td>
                  <td className="domain">{item.domain}</td>
                <td className="volume">{clientPerformanceApi.formatVolume(item.total_volume)}</td>
                <td className={getPerformanceClass(item.overall_inbox_pct, 'inbox')}>
                  {clientPerformanceApi.formatPercentage(item.overall_inbox_pct)}
                </td>
                <td className={getPerformanceClass(item.spam_rate, 'spam')}>
                  {clientPerformanceApi.formatPercentage(item.spam_rate)}
                </td>
                <td className={getPerformanceClass(item.or_pct, 'open')}>
                  {clientPerformanceApi.formatPercentage(item.or_pct)}
                </td>
                <td>{clientPerformanceApi.formatPercentage(item.cr_pct)}</td>
                <td className={getPerformanceClass(item.domain_reputation, 'reputation')}>
                  <span className="reputation-badge">{item.domain_reputation}</span>
                </td>
              </tr>
              {expandedRows.has(index) && (
                <tr className="expanded-row">
                  <td colSpan={10}>
                    <div className="expanded-content">
                      <div className="expanded-section">
                        <h4>Provider Breakdown</h4>
                        <div className="metric-grid">
                          <div className="metric-item">
                            <span className="metric-label">Gmail:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.gmail)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Yahoo:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.yahoo)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Microsoft:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.microsoft)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Others:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.others)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="expanded-section">
                        <h4>Inbox Placement by Provider</h4>
                        <div className="metric-grid">
                          <div className="metric-item">
                            <span className="metric-label">Gmail Inbox:</span>
                            <span className={`metric-value ${getPerformanceClass(item.gmail_inbox_pct, 'inbox')}`}>
                              {clientPerformanceApi.formatPercentage(item.gmail_inbox_pct)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Yahoo Inbox:</span>
                            <span className={`metric-value ${getPerformanceClass(item.yahoo_inbox_pct, 'inbox')}`}>
                              {clientPerformanceApi.formatPercentage(item.yahoo_inbox_pct)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Microsoft Inbox:</span>
                            <span className={`metric-value ${getPerformanceClass(item.microsoft_inbox_pct, 'inbox')}`}>
                              {clientPerformanceApi.formatPercentage(item.microsoft_inbox_pct)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="expanded-section">
                        <h4>Spam Rates by Provider</h4>
                        <div className="metric-grid">
                          <div className="metric-item">
                            <span className="metric-label">Gmail Spam:</span>
                            <span className={`metric-value ${getPerformanceClass(item.gmail_spam_pct, 'spam')}`}>
                              {clientPerformanceApi.formatPercentage(item.gmail_spam_pct)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Yahoo Spam:</span>
                            <span className={`metric-value ${getPerformanceClass(item.yahoo_spam_pct, 'spam')}`}>
                              {clientPerformanceApi.formatPercentage(item.yahoo_spam_pct)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Microsoft Spam:</span>
                            <span className={`metric-value ${getPerformanceClass(item.microsoft_spam_pct, 'spam')}`}>
                              {clientPerformanceApi.formatPercentage(item.microsoft_spam_pct)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="expanded-section">
                        <h4>IP Reputation</h4>
                        <div className="metric-grid">
                          <div className="metric-item">
                            <span className="metric-label">Total IPs:</span>
                            <span className="metric-value">{item.total_ips}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">High:</span>
                            <span className="metric-value performance-good">{item.high}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Medium:</span>
                            <span className="metric-value performance-warning">{item.medium}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Low:</span>
                            <span className="metric-value performance-critical">{item.low}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Bad:</span>
                            <span className="metric-value performance-critical">{item.bad}</span>
                          </div>
                        </div>
                      </div>

                      <div className="expanded-section">
                        <h4>Additional Metrics</h4>
                        <div className="metric-grid">
                          <div className="metric-item">
                            <span className="metric-label">Delivery Time:</span>
                            <span className="metric-value">{clientPerformanceApi.formatDecimal(item.delivery_time, 2)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">FBL:</span>
                            <span className="metric-value">{clientPerformanceApi.formatDecimal(item.fbl, 2)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Authentication:</span>
                            <span className="metric-value">{item.authen}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Delivery Errors:</span>
                            <span className="metric-value">{item.delivery_errors}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Sender Score:</span>
                            <span className="metric-value">{clientPerformanceApi.formatDecimal(item.sender_score, 1)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Talos Score:</span>
                            <span className="metric-value">{clientPerformanceApi.formatDecimal(item.talos, 2)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Proof Point:</span>
                            <span className="metric-value">{clientPerformanceApi.formatDecimal(item.proof_point, 2)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Blacklist:</span>
                            <span className="metric-value">{item.blacklist}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Deferrals:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.deferrals)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Soft Bounces:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.soft_bounces)}</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Queue:</span>
                            <span className="metric-value">{clientPerformanceApi.formatVolume(item.queue)}</span>
                          </div>
                        </div>
                      </div>

                      {item.tags && (
                        <div className="expanded-section">
                          <h4>Tags</h4>
                          <div className="tags-content">{item.tags}</div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(PerformanceTable);